/**
 * THE READ-THROUGH PANEL.
 *
 * The flow as an ordered list of sentences, in the order a caller hears them. It is the
 * plain-language answer to "what does my phone actually say", and it is the reason this
 * screen is usable without the canvas at all — a numbered list of sentences is something a
 * screen reader is good at, and a drag-and-drop graph is not.
 *
 * It shows the LIVE drawing, not the last save, and says so. An owner who moves a step and
 * then reads a panel describing the version from ten minutes ago has been lied to about the
 * one thing this panel exists to tell the truth about.
 */
import type { ReactNode } from 'react';

import { Badge, HelpTip, Icon } from '../index.ts';
import { readThrough, summarise } from './readthrough.ts';
import type { CompileIssue, Line } from './readthrough.ts';
import type { PlaybookStep } from '../../../../src/api/contract.ts';

export type ReadThroughProps = {
  steps: PlaybookStep[];
  /** Problems that stop it compiling. When there are any, `steps` is the last good version. */
  errors: CompileIssue[];
  warnings: CompileIssue[];
  /** True when the steps shown are the ones the phone is running, not a preview. */
  live: boolean;
  /**
   * Nothing has been drawn yet beyond the entry step the server creates.
   *
   * The compiler reports an empty flow as an error, which is right for the compiler and
   * wrong for a person: on a flow four seconds old this panel opened with a red alarm
   * reading "This drawing will not run yet — this flow does not do anything yet", under a
   * heading promising to tell them what the caller hears. The screen above already fixed
   * its own copy of this and this panel did not, so the first impression of the product was
   * still an alarm about a mistake nobody had had the chance to make.
   */
  blank: boolean;
  onFocusNode: (id: string) => void;
};

/**
 * The numbers count STEPS, not lines. A doctrine note ("the caller is never connected to a
 * technician") is an annotation on the step above it, and numbering it made a nine-step flow
 * read as fourteen — which is a lie about the length of the call.
 */
function numbered(lines: Line[]): { line: Line; number: number | null }[] {
  let n = 0;
  return lines.map((line) => {
    if (line.kind === 'rule') return { line, number: null };
    n += 1;
    return { line, number: n };
  });
}

export function ReadThrough({ steps, errors, warnings, live, blank, onFocusNode }: ReadThroughProps): ReactNode {
  const lines = readThrough(steps);
  const blocking = blank ? [] : errors;

  return (
    <section className="ndf-read" aria-label="What the caller hears">
      <header className="ndf-read-head">
        <div>
          <span className="nd-eyebrow">
            What the caller hears <HelpTip helpKey="flows.readthrough" />
          </span>
          <p className="ndf-read-summary">{summarise(steps)}</p>
        </div>
        {live ? (
          <Badge tone="ok" size="sm" dot>
            Answering now
          </Badge>
        ) : blank ? null : (
          <Badge tone="plain" size="sm">
            Not saved yet
          </Badge>
        )}
      </header>

      {blocking.length > 0 ? (
        <div className="ndf-read-blocked" role="alert">
          <p className="ndf-read-blocked-title">
            <Icon name="alert-triangle" size={13} /> This drawing will not run yet.
          </p>
          <ul>
            {blocking.map((issue) => (
              <li key={`${issue.nodeId ?? 'flow'}-${issue.message}`}>
                {issue.nodeId ? (
                  <button type="button" className="ndf-read-jump" onClick={() => onFocusNode(issue.nodeId as string)}>
                    {issue.message}
                    <Icon name="arrow-right" size={11} />
                  </button>
                ) : (
                  <span>{issue.message}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="ndf-read-blocked-foot">
            Nothing has been saved, so the phone is still answering with the last version that worked.
          </p>
        </div>
      ) : null}

      {lines.length === 0 ? (
        <p className="ndf-read-empty">
          Nothing to read yet. Add a step the caller hears and this fills in as you draw.
        </p>
      ) : (
        <ol className="ndf-read-lines">
          {numbered(lines).map(({ line, number }, index) => (
            <li
              key={`${index}-${line.text}`}
              className={`ndf-read-line ndf-read-line--${line.kind}`}
              data-depth={line.depth}
            >
              <span className="ndf-read-num" aria-hidden="true">
                {number ?? ''}
              </span>
              <span className="ndf-read-text">
                {line.text}
                {line.quote !== undefined ? (
                  <>
                    {': '}
                    <q className="ndf-read-quote">{line.quote}</q>
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      )}

      {warnings.length > 0 && !blank ? (
        <div className="ndf-read-warnings">
          <p className="nd-eyebrow">Worth a look</p>
          <ul>
            {warnings.map((issue) => (
              <li key={`${issue.nodeId ?? 'flow'}-${issue.message}`}>
                {issue.nodeId ? (
                  <button type="button" className="ndf-read-jump" onClick={() => onFocusNode(issue.nodeId as string)}>
                    {issue.message}
                    <Icon name="arrow-right" size={11} />
                  </button>
                ) : (
                  <span>{issue.message}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

export default ReadThrough;
